// Pure « Équipe » desk-read logic — the apps/staff logic.ts day-helper
// pattern, copied rather than shared (R4 is a front-end-only read surface;
// no second consumer yet to justify extracting a package — see the build
// brief §5 "menu data home" precedent).
import {
  recouchesFromOccupancy,
  todayIsoParis,
  type BoardDay,
  type BoardDayUpsert,
  type CoverageStatus,
  type Employee,
  type StaffMessage,
  type Team,
} from '@lemontaigu/staff';

export { frDayLabel, senderLabel } from '@lemontaigu/staff';

export function teamLabel(team: '' | Team | null): string {
  switch (team) {
    case 'menage':
      return 'Ménage';
    case 'receptionSalle':
      return 'Réception / Salle';
    case 'cuisine':
      return 'Cuisine';
    default:
      return '—';
  }
}

/** Team display order for the month presence columns (operator choice) — unassigned last. */
export const TEAM_DISPLAY_ORDER: (Team | null)[] = ['receptionSalle', 'menage', 'cuisine', null];

/** Employees grouped by team in display order, alpha within a team; empty teams dropped. */
export function groupEmployeesByTeam(employees: Employee[]): { team: Team | null; members: Employee[] }[] {
  return TEAM_DISPLAY_ORDER.map((team) => ({
    team,
    members: employees
      .filter((e) => (e.team ?? null) === team)
      .sort((a, b) => a.firstName.localeCompare(b.firstName, 'fr')),
  })).filter((g) => g.members.length > 0);
}

/**
 * Calendar day key ('2026-07-12') from a KEY-derived Date (e.g. `new
 * Date('${key}T12:00:00')` inside shiftDay) — noon-anchored arithmetic on an
 * already-local synthetic date, timezone-agnostic by construction. NEVER call
 * this on a live instant (`new Date()` or a real timestamp like sentAt /
 * clock_in) — that depends on the RUNTIME's configured timezone, which is not
 * guaranteed to be Europe/Paris (misconfigured device, CI, browser override).
 * Use `todayIsoParis` for that (it takes any Date, explicit Europe/Paris).
 */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** [start, end) ISO instants of a local calendar day, for the sent_at range query. */
export function dayRange(key: string): { fromIso: string; toIso: string } {
  const start = new Date(`${key}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { fromIso: start.toISOString(), toIso: end.toISOString() };
}

export function shiftDay(key: string, delta: number): string {
  const d = new Date(`${key}T12:00:00`); // noon dodges DST edges
  d.setDate(d.getDate() + delta);
  return dayKey(d);
}

export function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// ── Punch windows (auto-declare from the clock, 0046) ────────────────────────
// Clock instants are timestamptz; the third they fall in is Europe/Paris
// wall-clock, which is NOT guaranteed to be the runtime's zone — format
// explicitly (the dayKey/todayIsoParis rule, applied to time-of-day).
// parisHM now lives in @lemontaigu/staff — the server-side corpus job needs the
// same rule, and one time conversion must have one implementation. Re-exported
// under its original name so every caller here is untouched.
import { parisHM } from '@lemontaigu/staff';
export { parisHM };

/** A day's shift window from its punches. `open` = a punch has no clock-out yet (still on shift). */
export interface PunchWindow {
  start: string;
  /** Latest clock-out, or '23:00' when still open (see `open`). */
  end: string;
  open: boolean;
}

/** Earliest clock-in → latest clock-out for the day; open when any entry has no clock-out. */
export function punchWindowOf(entries: { clockIn: string; clockOut: string | null }[]): PunchWindow | null {
  if (entries.length === 0) return null;
  const ins = entries.map((e) => parisHM(e.clockIn)).sort();
  const open = entries.some((e) => !e.clockOut);
  const outs = entries.filter((e) => e.clockOut).map((e) => parisHM(e.clockOut as string)).sort();
  const start = ins[0];
  if (!start) return null;
  const end = open ? '23:00' : (outs[outs.length - 1] ?? start);
  return { start, end, open };
}

/** One real clock interval (Europe/Paris 'HH:MM'); `end` null = still clocked in. */
export interface PunchInterval {
  start: string;
  end: string | null;
}

/**
 * A day's punches AS THEY HAPPENED — one interval per entry, chronological. The
 * pointage realises a single planned activity in several intervals (ADR-0011
 * §8: Cuisine 10:30–12:30 + an afternoon hour + 18:30–21:00), so the Gantt
 * draws a segment per interval rather than one merged bar.
 */
export function punchIntervalsOf(entries: { clockIn: string; clockOut: string | null }[]): PunchInterval[] {
  return entries
    .map((e) => ({ start: parisHM(e.clockIn), end: e.clockOut ? parisHM(e.clockOut) : null }))
    .sort((a, b) => a.start.localeCompare(b.start));
}

export function fmtDayLabel(key: string): string {
  return new Date(`${key}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** Fold a Realtime insert into the loaded window: dedupe by id, keep sent order. */
export function mergeMessage(messages: StaffMessage[], incoming: StaffMessage): StaffMessage[] {
  if (messages.some((m) => m.id === incoming.id)) return messages;
  const next = [...messages, incoming];
  next.sort((a, b) => (a.sentAt < b.sentAt ? -1 : a.sentAt > b.sentAt ? 1 : a.id - b.id));
  return next;
}

/** True when a Realtime insert belongs to the currently displayed Europe/Paris day. */
export function belongsToDay(msg: StaffMessage, key: string): boolean {
  return todayIsoParis(new Date(msg.sentAt)) === key;
}

// ── Planning (S4-core: demand entry + coverage + declared-vs-clocked) ────────

/** The 60-second D+1 gesture's form state — strings, as typed (metrics v3, 2026-07-20). */
export interface DemandDraft {
  departures: string;
  arrivals: string;
  /** Occupation — rooms occupied that night (running chain, 0041). Recouches derive from it. */
  occupancy: string;
  breakfasts: string;
  /** Paniers repas — fed (0146). */
  packedLunches: string;
  /** DP — contracted demi-pension covers, fed (0146). The chef's sure number. */
  halfBoards: string;
  /** Hotel guests dining outside demi-pension, fed (0146). */
  dinersHotel: string;
  /** « Dîners Ext » — walk-ins / phone / online, TYPED. « Couverts » is
   *  DERIVED = halfBoards + dinersHotel + diners (0146). */
  diners: string;
  note: string;
}

export type DemandFieldKey =
  | 'arrivals'
  | 'departures'
  | 'occupancy'
  | 'breakfasts'
  | 'packedLunches'
  | 'halfBoards'
  | 'dinersHotel'
  | 'diners';

export interface DemandFieldSpec {
  key: DemandFieldKey;
  label: string;
  /** Computed fields are shown read-only — the desk enters base facts only. */
  computed?: boolean;
  /**
   * Written by the Vega feed, not by the desk (METRICS-FEED-BRIEF §6.1, M4).
   *
   * Rendered read-only for the reason §6.2 gives: "A field that accepts a value
   * and discards it at the next import is worse than a field that refuses it.
   * The receptionist types a correction, sees it accepted, and it disappears
   * hours later with no error." The importer ticks every MINUTE, so it would
   * not even be hours.
   *
   * This is coherent only because Vega is the operational system — anything the
   * desk could legitimately correct (a walk-in, a no-show, a room change) is
   * entered in Vega first. The moment that stops being true, this rule stops
   * being right, which is exactly what happens at the PMS cutover.
   */
  feedOwned?: boolean;
}

/**
 * « Charge » in the operator's reading order, split into the two rows the desk
 * thinks in: rooms first, then meals. Recouches and Couverts are DERIVED and
 * rendered read-only.
 *
 * Order, 2026-09-02: **Départs · Arrivées · Occupation**. It was Arrivées first
 * (2026-07-20) and briefly Départs first (2026-08-01); the desk's own sequence
 * is what leaves, what comes, what stays — and départs is the figure the
 * morning is planned around, so it leads.
 *
 * « Départs (à blancs) » lost its parenthetical the same day: it described what
 * a departure COSTS on a row that counts how many there ARE, and read as a
 * second figure. The cost has its own cell — « Charge <profil> » (0133).
 */
export const DEMAND_HOTEL_FIELDS: DemandFieldSpec[] = [
  { key: 'departures', label: 'Départs', feedOwned: true },
  { key: 'arrivals', label: 'Arrivées', feedOwned: true },
  { key: 'occupancy', label: 'Occupation', feedOwned: true },
];

export const DEMAND_RESTAURANT_FIELDS: DemandFieldSpec[] = [
  { key: 'breakfasts', label: 'Petits-déjeuners', feedOwned: true },
  { key: 'packedLunches', label: 'Paniers repas', feedOwned: true },
  // ── The dinner split (0146) — why this is three cells and not one ────────
  //
  // « DP » is the chef's SURE number: contracted demi-pension covers, which he
  // builds a menu from and extrapolates the rest off occupancy. Until 0146 the
  // board added a room-only guest who booked a table into the same figure, so
  // the number he planned from was not the number he could rely on.
  { key: 'halfBoards', label: 'DP', feedOwned: true },
  { key: 'dinersHotel', label: 'Dîners', feedOwned: true },
  // The ONE surviving manual input. Operator, 2026-09-01: "the only manual
  // metric is the number of « Dinners » (… non Demi-Pension covers / walk-ins)
  // because they are not tracked inside Vega and come from several sources
  // (phone + LS online reservations)".
  //
  // Vega CAN supply it — a walk-in is a restaurant booking like any other — and
  // it is fed as `dinersExternalBooked` and shown beside this cell as a hint.
  // It stays typed because the fed value is a FLOOR, not a count: the staff
  // work from a printout and mostly do not enter walk-ins (15 in two years).
  // Q7 = A, operator 2026-09-09.
  { key: 'diners', label: 'Dîners Ext' },
];

/** Every demand number, feed-owned or not — validation and the week grid. */
export const DEMAND_FIELDS: DemandFieldSpec[] = [...DEMAND_HOTEL_FIELDS, ...DEMAND_RESTAURANT_FIELDS];

/** The tooltip on every feed-owned cell. One string, shared by the day and
 *  week views, so the two cannot drift into explaining the same thing
 *  differently — and so the reason a field cannot be typed into is always one
 *  hover away rather than folklore. */
export const FEED_HINT =
  'Écrit par le relevé Vega, pas à la main. Corrigez dans Vega : la prochaine ' +
  'importation (moins d’une minute) reprend la valeur.';

/** « 10 (2 E) » under Occupation — personnes présentes, dont enfants. The
 *  under-reporting is stated because it is real (METRICS-FEED-BRIEF §4.4: the
 *  source omits children on ~12% of rows) and a desk that does not know it
 *  would read a low count as a fact rather than as a floor. */
export const GUESTS_HINT =
  'Personnes présentes cette nuit-là, dont enfants (E) — écrit par le relevé ' +
  'Vega. Les enfants sont sous-déclarés à la source : le chiffre est un minimum, ' +
  'jamais une estimation.';

/** The fields the desk may still type. Derived from the specs rather than
 *  listed again: a second list is a second place to forget `feedOwned`. */
export const DEMAND_EDITABLE_FIELDS: DemandFieldSpec[] = DEMAND_FIELDS.filter((f) => !f.feedOwned);

/** Couverts = DP + dîners hôtel + dîners ext (0146) — the figure the restaurant
 *  drivers read. Three terms since the dinner split: dropping the middle one
 *  would make the live total under-report by exactly the à-la-carte hotel
 *  diners, and only while the desk was typing. */
export function coversOf(d: DemandDraft): number {
  return demandNum(d.halfBoards) + demandNum(d.dinersHotel) + demandNum(d.diners);
}

export function demandDraftFromDay(day: BoardDay | null): DemandDraft {
  return {
    departures: day ? String(day.departures) : '',
    arrivals: day ? String(day.arrivals) : '',
    occupancy: day && day.occupancy != null ? String(day.occupancy) : '',
    breakfasts: day ? String(day.breakfasts) : '',
    packedLunches: day ? String(day.packedLunches) : '',
    halfBoards: day ? String(day.halfBoards) : '',
    dinersHotel: day ? String(day.dinersHotel) : '',
    diners: day ? String(day.diners) : '',
    note: day?.note ?? '',
  };
}

/** Parse a count field ('' → 0). */
export function demandNum(value: string): number {
  return value.trim() === '' ? 0 : Number(value.trim());
}

/** Null when valid — every count must be empty (= 0) or a non-negative integer. */
export function demandDraftError(d: DemandDraft): string | null {
  const fields: [string, string][] = DEMAND_FIELDS.map((f) => [f.label, d[f.key]] as [string, string]);
  for (const [label, value] of fields) {
    const v = value.trim();
    if (v !== '' && !/^\d+$/.test(v)) return `« ${label} » doit être un nombre entier.`;
  }
  return null;
}

/**
 * Draft → board_days upsert payload. Occupancy is the authoritative nightly
 * figure (null = not seeded); « recouches » is DERIVED from it (occupancy −
 * arrivals) and written into `stayovers` so the coverage driver and existing
 * readers keep working. The caller resolves the effective occupancy (typed
 * value, or the running-chain suggestion) into the draft before saving.
 */
/**
 * Draft (form strings) → the DOMAIN write shape.
 *
 * Returned snake_case rows until ladder step 7, which put PostgREST's column
 * names inside the desk's form module. The mapping to columns now lives in
 * `SupabaseBoardWriteStore`; this function's job is parsing and derivation,
 * which is genuinely the view's.
 */
export function demandParams(d: DemandDraft, date: string): BoardDayUpsert {
  const arrivals = demandNum(d.arrivals);
  const occupancy = d.occupancy.trim() === '' ? null : Number(d.occupancy.trim());
  return {
    date,
    departures: demandNum(d.departures),
    arrivals,
    occupancy,
    stayovers: occupancy == null ? 0 : recouchesFromOccupancy(occupancy, arrivals),
    breakfasts: demandNum(d.breakfasts),
    halfBoards: demandNum(d.halfBoards),
    diners: demandNum(d.diners),
    // Couverts is DERIVED (v3) but still stored, so every existing reader and
    // demand formula keeps working — same pattern as stayovers in 0041.
    covers: coversOf(d),
    note: d.note.trim() === '' ? null : d.note.trim(),
  };
}

/** Postgres time ('14:00' / '14:00:00') → display '14:00'. */
export function fmtWindowTime(time: string): string {
  return time.slice(0, 5);
}

export function coverageStatusLabel(status: CoverageStatus): string {
  switch (status) {
    case 'covered':
      return 'Couvert';
    case 'gap':
      return 'À pourvoir';
    case 'waived':
      return 'Dispensé';
    case 'noDemand':
      return 'Pas de besoins';
  }
}

/** A day's shifts for one employee → 'pointé 07:12–11:03 · 14:02–…' (or ''). */
export function shiftSummary(entries: { clockIn: string; clockOut: string | null }[]): string {
  if (entries.length === 0) return '';
  const parts = [...entries]
    .sort((a, b) => (a.clockIn < b.clockIn ? -1 : 1))
    .map((e) => `${fmtTime(e.clockIn)}–${e.clockOut ? fmtTime(e.clockOut) : '…'}`);
  return `pointé ${parts.join(' · ')}`;
}

// ── Week / month projections (S4 — weekly load input, Gantt board, month) ────

/** Monday of the week containing `key` (French business week). */
export function weekStartKey(key: string): string {
  const d = new Date(`${key}T12:00:00`);
  const mondayOffset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - mondayOffset);
  return dayKey(d);
}

/** The 7 day-keys of the week starting at `startKey` (a Monday). */
export function weekDays(startKey: string): string[] {
  return Array.from({ length: 7 }, (_, i) => shiftDay(startKey, i));
}

/** 'lun. 20' — the Gantt column header. */
export function fmtShortDayLabel(key: string): string {
  return new Date(`${key}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });
}

/** '2026-07-18' → '2026-07-01' (first day of its month). */
export function monthStart(key: string): string {
  return `${key.slice(0, 7)}-01`;
}

export function shiftMonthStart(key: string, delta: number): string {
  const d = new Date(`${monthStart(key)}T12:00:00`);
  d.setMonth(d.getMonth() + delta);
  return monthStart(dayKey(d));
}

export function fmtMonthLabel(key: string): string {
  return new Date(`${monthStart(key)}T12:00:00`).toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric',
  });
}

/** Every day-key of the month containing `key`, in ascending order. */
export function monthDays(key: string): string[] {
  const first = monthStart(key);
  const nextMonth = shiftMonthStart(key, 1);
  const days: string[] = [];
  let cursor = first;
  while (cursor < nextMonth) {
    days.push(cursor);
    cursor = shiftDay(cursor, 1);
  }
  return days;
}

/** Saturday or Sunday (noon anchor dodges DST edges). */
export function isWeekend(key: string): boolean {
  const wd = new Date(`${key}T12:00:00`).getDay();
  return wd === 0 || wd === 6;
}

/**
 * Calendar weeks (Monday-first) of the month containing `key` — day keys for
 * in-month cells, null padding for the leading/trailing out-month cells.
 */
export function monthWeeks(key: string): (string | null)[][] {
  const first = monthStart(key);
  const nextMonth = shiftMonthStart(key, 1);
  const weeks: (string | null)[][] = [];
  let cursor = weekStartKey(first);
  while (cursor < nextMonth) {
    const week = weekDays(cursor).map((d) => (d >= first && d < nextMonth ? d : null));
    weeks.push(week);
    cursor = shiftDay(cursor, 7);
  }
  return weeks;
}

/**
 * A day counts as PLANNED once anything is entered for it (load, at least one
 * declaration, or an operator assignment — 0039). Unplanned future days render
 * neutral — 9 red flags on every empty day three weeks out would be noise, not
 * information.
 */
export function isPlannedDay(
  hasBoardDay: boolean,
  declarationCount: number,
  assignmentCount = 0,
): boolean {
  return hasBoardDay || declarationCount > 0 || assignmentCount > 0;
}

// ── Day-Gantt label placement (operator request 2026-08-09) ─────────────────

/** Which side of its bar a row's label block (time · chips · badge) sits on. */
export type BarContentSide = 'after' | 'before';

/**
 * Where to print a Gantt row's label block.
 *
 * The operator's rule: AFTER the bar for a morning activity, BEFORE it for an
 * afternoon/evening one. Both put the text in the row's EMPTY half rather than
 * on top of the coloured bar, which is what stopped « 21,5 éq. » from being
 * clipped at the bar's edge.
 *
 * The rule is stated on the START time, not on the bar's pixel position,
 * because that is how the operator described it and how the day actually reads:
 * « Nettoyage » is a morning job whether it runs to 13:12 or 15:00.
 *
 * The one refinement the literal rule needs is a ROOM check. « Cuisine
 * 10:00–22:00 » starts in the morning but ends hard against the right edge, so
 * printing after it would run the text off the track — the exact clipping this
 * change exists to remove. When the preferred side has less than `minRoomPct`
 * of the track, the block takes the other side.
 */
export function barContentSide(
  windowStart: string,
  leftPct: number,
  widthPct: number,
  minRoomPct = 14,
): BarContentSide {
  const [h = 0] = windowStart.split(':').map(Number);
  const preferred: BarContentSide = h < 12 ? 'after' : 'before';
  const roomAfter = Math.max(0, 100 - (leftPct + widthPct));
  const roomBefore = Math.max(0, leftPct);
  if (preferred === 'after') return roomAfter >= minRoomPct || roomBefore < minRoomPct ? 'after' : 'before';
  return roomBefore >= minRoomPct || roomAfter < minRoomPct ? 'before' : 'after';
}


// ── « importé à … » — the feed's own freshness (M4, RUNBOOK §20.7) ──────────
//
// After M4 the demand figures always render, so a feed that died last Tuesday
// looks exactly like one that ran a minute ago. §20.7: "A feed nobody can tell
// has stopped is a manual entry nobody remembers to do, wearing a better
// disguise." The decisions below are what stop that, so they live here — pure,
// tested — rather than inside a component.

export interface FeedProvenanceView {
  importedAt: string;
  applied: boolean;
  refusalReason: string | null;
}

export type FeedStampTone = 'fresh' | 'stale' | 'refused' | 'unknown';

export interface FeedStamp {
  text: string;
  tone: FeedStampTone;
  title: string;
}

const FEED_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `null` = the feed has never run. `'unknown'` = we could not find out (no port
 * bound, or the read failed). Those are DIFFERENT answers and neither may
 * render as freshness — that conflation is the whole failure mode.
 */
export function feedStamp(
  provenance: FeedProvenanceView | null | 'unknown',
  now: number,
): FeedStamp {
  if (provenance === 'unknown') {
    return {
      text: 'import inconnu',
      tone: 'unknown',
      title: 'Impossible de lire l’état du relevé.',
    };
  }
  if (provenance === null) {
    return {
      text: 'aucun import',
      tone: 'stale',
      title: 'Aucun import n’a jamais eu lieu — les chiffres ne viennent pas du relevé.',
    };
  }

  const at = new Date(provenance.importedAt);
  const age = now - at.getTime();
  // Relative once it stops being today: a bare clock time on a four-day-old
  // import reads as this morning, which is the disguise itself.
  const when =
    age < FEED_DAY_MS
      ? `importé à ${at.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
      : `importé il y a ${Math.floor(age / FEED_DAY_MS)} j`;

  if (!provenance.applied) {
    return {
      text: `${when} — dernier essai refusé`,
      tone: 'refused',
      title:
        `Le dernier essai d’import a été refusé : ${provenance.refusalReason ?? 'raison non précisée'}. ` +
        'Les chiffres affichés proviennent de l’import précédent.',
    };
  }
  return {
    text: when,
    tone: age < FEED_DAY_MS ? 'fresh' : 'stale',
    title: 'Les chiffres Hôtel viennent du relevé Vega, pas d’une saisie.',
  };
}
